import { ClipboardList } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourseSignups, getCourses } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { StudentStatusPageShell } from '../../components/dashboard/StudentStatusPageShell';
import { formatDate } from '../../utils/date';

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || '-';
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getDropDate(item) {
  return toDate(item?.droppedAt)
    || toDate(item?.dropAt)
    || toDate(item?.notJoiningAt)
    || toDate(item?.updatedAt)
    || toDate(item?.createdAt)
    || null;
}

export function DroppedStudentsPage() {
  const { activeTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setSignups([]);
      setCourses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextSignups, nextCourses] = await Promise.all([
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
      ]);
      setSignups(nextSignups);
      setCourses(nextCourses);
    } catch (error) {
      console.error(error);
      setSignups([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const courseLookup = useMemo(
    () => new Map(courses.map((course) => [course.id, course])),
    [courses],
  );

  const rows = useMemo(() => {
    const search = normalizeText(searchTerm);

    return signups
      .filter((item) => normalizeText(item.status) === 'dropped')
      .map((item) => ({
        ...item,
        courseName: item.courseName || courseLookup.get(item.courseId)?.name || '-',
        droppedDate: getDropDate(item),
        dropReason: item.dropReason || item.notJoiningReason || item.learningBreakReason || item.note || '',
      }))
      .filter((item) => {
        if (!search) return true;
        return [
          formatStudentName(item),
          item.courseName,
          item.intakeLabel,
          item.dropReason,
          item.email,
          item.phone,
        ].some((value) => normalizeText(value).includes(search));
      })
      .sort((a, b) => {
        const left = a.droppedDate || new Date(0);
        const right = b.droppedDate || new Date(0);
        return right - left;
      });
  }, [courseLookup, searchTerm, signups]);

  const insightCards = [
    { label: 'Learners withdrawn', value: rows.length, note: 'Students who have left a learning path.', tone: 'dropped' },
    { label: 'Courses affected', value: new Set(rows.map((item) => item.courseName).filter(Boolean)).size, note: 'Learning paths needing review.', tone: 'blue' },
    { label: 'Reasons captured', value: rows.filter((item) => item.dropReason).length, note: 'Records with context for your team.', tone: 'green' },
  ];

  if (loading) {
    return <LoadingState label="Loading dropped students..." />;
  }

  return (
    <StudentStatusPageShell
      title="Dropped Students"
      description="Understand where learners leave, keep the reason and context close, and use the pattern to make your learning experience stronger."
      kicker="Students"
      stats={[]}
      insightCards={insightCards}
      className="dropped-students-page"
      tableTitle="Dropped Students"
      tableMeta={`${rows.length} dropped students`}
      tableDescription="Students who have dropped out of the academy."
      searchId="dropped-students-search"
      searchValue={searchTerm}
      onSearchChange={(event) => setSearchTerm(event.target.value)}
      searchPlaceholder="Search student, course, reason..."
      rows={rows}
      columns={[
        {
          key: 'student',
          label: 'Student',
          render: (item) => (
            <div className="appsStudentCell">
              <div className="application-avatar" aria-hidden="true">
                {String(formatStudentName(item)).trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'ST'}
              </div>
              <div className="application-applicant-copy">
                <strong>{formatStudentName(item)}</strong>
                <span>{item.intakeLabel || 'Open intake'}</span>
              </div>
            </div>
          ),
        },
        { key: 'courseName', label: 'Course', render: (item) => item.courseName || '-' },
        { key: 'intakeLabel', label: 'Cohort', render: (item) => item.intakeLabel || 'Open intake' },
        { key: 'droppedDate', label: 'Dropped On', render: (item) => (item.droppedDate ? formatDate(item.droppedDate) : '-') },
        { key: 'dropReason', label: 'Reason', render: (item) => item.dropReason || '-' },
        {
          key: 'view',
          label: 'View student',
          render: (item) => (
            <Button to={`/view-student?applicationId=${encodeURIComponent(item.id)}`} variant="secondary" className="appsViewBtn">
              View
            </Button>
          ),
        },
      ]}
      empty={(
        <EmptyState
          title="No dropped students yet."
          message="Students marked as dropped will appear here."
          icon={ClipboardList}
        />
      )}
    />
  );
}
